import { useState } from 'react';
import {
  PrimaryButton,
  SecondaryButton,
  TextButton,
  StatusPill,
  type StatusLevel,
} from '@/lib';
import { Gated, useCapability } from '@/app/CapabilitiesProvider';
import type { ApiClients } from '@/api/client';
import {
  usePairSensor,
  useAssignSensorToAsset,
  useCaptureBaseline,
  useActivateSensor,
} from './queries';

export type S7CommissioningProps = {
  clients: ApiClients;
  /** Optional pre-selected asset (e.g. routed in from S3 "Add sensor"). */
  presetAssetId?: string;
};

type Step = 1 | 2 | 3 | 4;

type StepDef = {
  num: Step;
  title: string;
};

const STEPS: ReadonlyArray<StepDef> = [
  { num: 1, title: 'Pair sensor' },
  { num: 2, title: 'Assign to asset' },
  { num: 3, title: 'Capture baseline' },
  { num: 4, title: 'Activate' },
];

/**
 * S7 Commissioning. Capability-gated 4-step stepper wired to the
 * commissioning endpoints. Each step calls a mutation and only
 * advances on success — failures keep the operator on the current
 * step with the server's error visible.
 *
 *   1. Pair sensor   POST /v1/sensors/pair                sensor.pair
 *   2. Assign        POST /v1/assets/{id}/sensors         asset.assign_sensor
 *   3. Baseline      POST /v1/sensors/{id}/baseline       sensor.commission
 *   4. Activate      POST /v1/sensors/{id}/activate       sensor.commission
 *
 * The pair step's response carries a pairing_token but the assignment
 * step takes the sensorId directly — for now those are decoupled
 * because the gateway doesn't model the pair→sensor lifecycle yet.
 * The operator types the sensorId at step 2; once the pair-to-sensor
 * binding lands server-side this UI step becomes a hidden auto-fill.
 */
export function S7Commissioning({ clients, presetAssetId }: S7CommissioningProps) {
  const [step, setStep] = useState<Step>(1);
  const [serial, setSerial] = useState('');
  const [pairingToken, setPairingToken] = useState<string | null>(null);
  const [assetId, setAssetId] = useState(presetAssetId ?? '');
  const [sensorId, setSensorId] = useState('');
  const [position, setPosition] = useState('');
  const [baselineCaptured, setBaselineCaptured] = useState(false);
  const [activated, setActivated] = useState(false);

  const canCommission = useCapability('sensor.commission');

  const pair = usePairSensor(clients);
  const assign = useAssignSensorToAsset(clients);
  const baseline = useCaptureBaseline(clients);
  const activate = useActivateSensor(clients);

  return (
    <div className="flex flex-col gap-sp-6">
      <header>
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Field workflow
        </p>
        <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">
          Sensor commissioning
        </h1>
        <p className="mt-sp-1 text-fs-meta text-gray-700">
          Pair a sensor, assign it to an asset position, capture a baseline,
          then activate it for live monitoring.
        </p>
      </header>

      <StepperHeader currentStep={step} />

      {step === 1 ? (
        <Gated capability="sensor.pair">
          <StepCard title="1 · Pair sensor">
            <p className="text-fs-body text-gray-700">
              Enter the sensor serial or MAC address. The gateway will broadcast
              a pair request and listen for the handshake reply.
            </p>
            <label className="mt-sp-3 flex flex-col gap-sp-1 text-fs-body text-gray-900">
              Serial / MAC
              <input
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                placeholder="e.g. PDM-S-04A1B2"
                className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 font-mono text-fs-body text-gray-900"
              />
            </label>
            {pairingToken ? (
              <p className="mt-sp-3 text-fs-meta text-gray-700">
                Paired. Token{' '}
                <code className="font-mono">{pairingToken}</code>.
              </p>
            ) : null}
            <StepFooter
              advanceLabel={pair.isPending ? 'Pairing…' : 'Pair sensor'}
              advanceDisabled={serial.trim().length < 6 || pair.isPending}
              onAdvance={() =>
                pair.mutate(
                  { serial: serial.trim() },
                  {
                    onSuccess: (data) => {
                      setPairingToken(data.pairing_token);
                      // Auto-fill sensorId from the pair response so
                      // the operator doesn't retype it at step 2.
                      if (data.sensorId) setSensorId(data.sensorId);
                      setStep(2);
                    },
                  },
                )
              }
              onBack={undefined}
              error={pair.isError ? String(pair.error) : null}
            />
          </StepCard>
        </Gated>
      ) : null}

      {step === 2 ? (
        <Gated capability="asset.assign_sensor">
          <StepCard title="2 · Assign to asset">
            <p className="text-fs-body text-gray-700">
              Choose the asset and bearing/motor position this sensor will
              monitor.
            </p>
            <div className="mt-sp-3 grid grid-cols-1 gap-sp-3 sm:grid-cols-2">
              <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
                Asset
                <input
                  value={assetId}
                  onChange={(e) => setAssetId(e.target.value)}
                  placeholder="asset_… (use Search for picker)"
                  className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 font-mono text-fs-body text-gray-900"
                />
              </label>
              <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
                Sensor ID
                <input
                  value={sensorId}
                  onChange={(e) => setSensorId(e.target.value)}
                  placeholder="sensor_…"
                  className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 font-mono text-fs-body text-gray-900"
                />
              </label>
              <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900 sm:col-span-2">
                Position
                <input
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="e.g. Drive-end bearing"
                  className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
                />
              </label>
            </div>
            <StepFooter
              advanceLabel={assign.isPending ? 'Assigning…' : 'Continue'}
              advanceDisabled={
                !assetId.trim() ||
                !sensorId.trim() ||
                !position.trim() ||
                assign.isPending
              }
              onAdvance={() =>
                assign.mutate(
                  {
                    assetId: assetId.trim(),
                    sensorId: sensorId.trim(),
                    position: position.trim(),
                  },
                  { onSuccess: () => setStep(3) },
                )
              }
              onBack={() => setStep(1)}
              error={assign.isError ? String(assign.error) : null}
            />
          </StepCard>
        </Gated>
      ) : null}

      {step === 3 ? (
        <Gated capability="sensor.commission">
          <StepCard title="3 · Capture baseline">
            <p className="text-fs-body text-gray-700">
              Hold the asset at nominal operating conditions and capture 30
              seconds of healthy reference data. The baseline anchors every
              future FFT comparison.
            </p>
            <div className="mt-sp-3 flex items-center gap-sp-3">
              <PrimaryButton
                label={
                  baseline.isPending
                    ? 'Capturing…'
                    : baselineCaptured
                      ? 'Re-capture baseline'
                      : 'Capture 30s baseline'
                }
                onClick={() =>
                  baseline.mutate(
                    { sensorId: sensorId.trim(), durationSec: 30 },
                    { onSuccess: () => setBaselineCaptured(true) },
                  )
                }
                disabled={baseline.isPending}
              />
              {baselineCaptured ? (
                <StatusPill level={'healthy' as StatusLevel} label="BASELINE READY" />
              ) : null}
            </div>
            <StepFooter
              advanceLabel="Continue"
              advanceDisabled={!baselineCaptured}
              onAdvance={() => setStep(4)}
              onBack={() => setStep(2)}
              error={baseline.isError ? String(baseline.error) : null}
            />
          </StepCard>
        </Gated>
      ) : null}

      {step === 4 ? (
        <Gated capability="sensor.commission">
          <StepCard title="4 · Activate">
            <p className="text-fs-body text-gray-700">
              Flip the sensor to live monitoring. Alerts and S5 analysis become
              available immediately; the first health score lands after the
              service has processed ~1 minute of telemetry.
            </p>
            <div className="mt-sp-3 flex items-center gap-sp-3">
              <PrimaryButton
                label={
                  activate.isPending
                    ? 'Activating…'
                    : activated
                      ? 'Activated ✓'
                      : 'Activate sensor'
                }
                onClick={() =>
                  activate.mutate(
                    { sensorId: sensorId.trim() },
                    { onSuccess: () => setActivated(true) },
                  )
                }
                disabled={activated || !canCommission || activate.isPending}
              />
              {activated ? (
                <StatusPill level={'healthy' as StatusLevel} label="LIVE" />
              ) : null}
            </div>
            {activated ? (
              <p className="mt-sp-3 text-fs-meta text-gray-700">
                Sensor <code className="font-mono">{sensorId}</code> is
                monitoring <code className="font-mono">{assetId}</code> at{' '}
                {position}. Open Signal Analysis to inspect the first traces.
              </p>
            ) : null}
            <StepFooter
              advanceLabel="Finish"
              advanceDisabled={!activated}
              onAdvance={() => setStep(1)}
              onBack={() => setStep(3)}
              error={activate.isError ? String(activate.error) : null}
            />
          </StepCard>
        </Gated>
      ) : null}
    </div>
  );
}

function StepperHeader({ currentStep }: { currentStep: Step }) {
  return (
    <ol
      aria-label="Commissioning progress"
      className="flex items-center gap-sp-3 text-fs-meta"
    >
      {STEPS.map((s, i) => {
        const done = currentStep > s.num;
        const current = currentStep === s.num;
        const dot = done
          ? 'bg-healthy text-healthy-fg'
          : current
            ? 'bg-info text-info-fg'
            : 'bg-gray-300 text-gray-700';
        return (
          <li key={s.num} className="flex items-center gap-sp-3">
            <span
              aria-hidden
              className={`flex h-6 w-6 items-center justify-center rounded-r-chip text-fs-meta font-medium ${dot}`}
            >
              {done ? '✓' : s.num}
            </span>
            <span
              className={
                current
                  ? 'font-medium text-gray-900'
                  : done
                    ? 'text-gray-900'
                    : 'text-gray-700'
              }
            >
              {s.title}
            </span>
            {i < STEPS.length - 1 ? (
              <span aria-hidden className="ml-sp-1 h-px w-sp-6 bg-gray-300" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function StepCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      aria-labelledby={`step-${title}`}
      className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
    >
      <h2
        id={`step-${title}`}
        className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
      >
        {title}
      </h2>
      <div className="mt-sp-3">{children}</div>
    </section>
  );
}

function StepFooter({
  advanceLabel,
  advanceDisabled,
  onAdvance,
  onBack,
  error,
}: {
  advanceLabel: string;
  advanceDisabled: boolean;
  onAdvance: () => void;
  onBack?: () => void;
  error?: string | null;
}) {
  return (
    <div className="mt-sp-6 flex flex-col gap-sp-2">
      <div className="flex items-center gap-sp-3">
        <PrimaryButton
          label={advanceLabel}
          onClick={onAdvance}
          disabled={advanceDisabled}
        />
        {onBack ? <SecondaryButton label="Back" onClick={onBack} /> : null}
        <TextButton label="Cancel" onClick={() => window.history.back()} />
      </div>
      {error ? (
        <span role="alert" className="text-fs-meta text-critical">
          {error}
        </span>
      ) : null}
    </div>
  );
}
